/**
 * AgentChatPanel
 * Picker: Ask Docs (din-chatbot.js) oder CO₂ Berechnung (din-co2-chat.js),
 * beide eingebettet im Side Panel.
 */

import { useState, useEffect, useRef, type CSSProperties } from 'react'

declare global {
  interface Window {
    dinChatbotMount?: (el: HTMLElement) => void
    dinCo2ChatMount?: (el: HTMLElement) => void
  }
}

type Mode = 'picker' | 'docs' | 'co2'

export function AgentChatPanel() {
  const [mode, setMode] = useState<Mode>('picker')
  const mountRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!mountRef.current) return
    if (mode === 'docs' && window.dinChatbotMount) {
      window.dinChatbotMount(mountRef.current)
    } else if (mode === 'co2' && window.dinCo2ChatMount) {
      window.dinCo2ChatMount(mountRef.current)
    }
  }, [mode])

  if (mode === 'picker') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '0.75em',
        padding: '1.5em',
      }}>
        <p style={{ fontSize: '0.75em', color: '#6b7280', marginBottom: '0.5em', textAlign: 'center' }}>
          Wählen Sie einen Agenten
        </p>
        <button
          onClick={() => setMode('docs')}
          style={pickerBtnStyle}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f0f9ff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#3b82f6' }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#e5e7eb' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          <span style={{ fontSize: '0.8125em', fontWeight: 600, color: '#111827' }}>Ask Docs</span>
          <span style={{ fontSize: '0.6875em', color: '#6b7280' }}>Fragen zu Dokumenten stellen</span>
        </button>
        <button
          onClick={() => setMode('co2')}
          style={pickerBtnStyle}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f0fdf4'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#22c55e' }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#e5e7eb' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/>
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
          </svg>
          <span style={{ fontSize: '0.8125em', fontWeight: 600, color: '#111827' }}>CO₂ Berechnung</span>
          <span style={{ fontSize: '0.6875em', color: '#6b7280' }}>DIN SPEC 91506 Expert</span>
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5em',
        padding: '0.375em 0.625em',
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0,
      }}>
        <button
          onClick={() => setMode('picker')}
          title="Zurück"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '1.75em', height: '1.75em', border: 'none', background: 'transparent',
            cursor: 'pointer', color: '#6b7280', borderRadius: '0.25em',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6' }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <span style={{ fontSize: '0.75em', fontWeight: 600, color: '#374151' }}>
          {mode === 'docs' ? 'Ask Docs' : 'CO₂ Berechnung'}
        </span>
      </div>
      <div
        ref={mountRef}
        id="agent-panel-mount"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      />
    </div>
  )
}

const pickerBtnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.375em',
  width: '100%',
  maxWidth: '220px',
  padding: '1em',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5em',
  background: '#fff',
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s',
}
